import React, { useCallback, useState } from 'react'

import { setOrderSearchResult } from '@/entities/userInterface/thunks/setOrderSearchResult'
import { Icon, IconButton } from '@/shared/components'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { SearchResult } from '@/shared/components/SearchBar/models/SearchResult'
import { AllSearchTypes, SearchType } from '@/shared/components/SearchBar/models/SearchType'
import { SearchBar } from '@/shared/components/SearchBar/SearchBar'
import { SearchResultOption } from '@/shared/components/SearchBar/SearchResultOption'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faFilter } from '@fortawesome/pro-solid-svg-icons'
import { Badge, Popover, styled } from '@mui/material'

import { useMosaicLayoutContext } from '../MosaicLayout/MosaicLayoutContext'
import { DateRangeFilter } from './DateRangeFilter'
import { useOrderSearchData } from './libs/useOrderSearchData'

export type OrderSearchBarProps = {
  //
}

export const OrderSearchBar: React.FC<OrderSearchBarProps> = () => {
  const dispatch = useAppDispatch()
  const { spawnWidgetInTopRightCorner } = useMosaicLayoutContext()
  const { loading, results, filters, setFilters, searchOrders, hasActiveFilters } = useOrderSearchData()

  // Filter button state
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const isFilterOpen = Boolean(anchorEl)

  const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const onFilterClose = () => {
    setAnchorEl(null)
  }

  // On search
  const onSearch = useCallback(async (query: string, type: SearchType) => {
    await searchOrders(query, type)
  }, [searchOrders])

  const onOrderSelect = (option: SearchResult) => {
    if (!option.orderId) return

    dispatch(setOrderSearchResult(option))
      .unwrap()
      .then(() => {
        if (option.scheduleId && option.shiftId) {
          spawnWidgetInTopRightCorner(`${option.isPlanning ? TREE_WIDGETS.PlanningShiftTable : TREE_WIDGETS.RunsheetShiftTable}-${option.shiftId}`)
        }
        else if (option.scheduleId) {
          spawnWidgetInTopRightCorner(`${option.isPlanning ? TREE_WIDGETS.ScenarioUnassignedOrders : TREE_WIDGETS.RunsheetUnassignedOrders}-${option.scheduleId}`)
        }
      })
  }

  return (
    <StyledContainer>
      <SearchBar
        searchTypes={AllSearchTypes}
        defaultSearchType="orderNumber"
        options={results}
        loading={loading}
        onSearch={onSearch}
        placeholder={'Search Order...'}
        renderOption={(_props, option) => (
          <SearchResultOption
            option={option as SearchResult}
            onClick={() => {
              onOrderSelect(option as SearchResult)
            }}
          />
        )}
      />

      <StyledFilterButton
        onClick={onFilterClick}
        size="small"
      >
        <Badge color="primary" variant="dot" invisible={!hasActiveFilters}>
          <StyledFilterIcon icon={faFilter} />
        </Badge>
      </StyledFilterButton>
      <Popover
        open={isFilterOpen}
        anchorEl={anchorEl}
        onClose={onFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <DateRangeFilter
          filters={filters}
          onFiltersChange={setFilters}
          disabled={loading}
        />
      </Popover>
    </StyledContainer>
  )
}

const StyledContainer = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)}
  
  & .MuiOutlinedInput-root {
    background-color: ${({ theme }) => theme.palette.background.paper};
  }
`

const StyledFilterButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 0;
  background-color: ${({ theme }) => theme.palette.background.paper};
  
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`

const StyledFilterIcon = styled(Icon)`
  font-size: ${props => props.theme.typography.pxToRem(14)};
`
